import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CoreService } from '../../../../services/core.service';
import { SectionGridComponent } from '../../components/section-grid/section-grid.component';
import { FooterGridComponent } from '../../components/footer-grid/footer-grid.component';

@Component({
  selector: 'app-edit-site',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SectionGridComponent,
    FooterGridComponent,
  ],
  templateUrl: './edit-site.component.html',
  styleUrl: './edit-site.component.scss'
})
export class EditSiteComponent implements OnInit, OnDestroy {
  @Input() siteID!: number;
  @Input() embeddedMode = false;
  @Input() hasBorder = true;
  @Input() companyID!: number;

  siteForm: FormGroup;
  isSubmitting = false;
  isLoading = true;

  // Inline messages
  successMessage = '';
  errorMessage = '';

  // File handling
  fullLogoFile: File | null = null;
  thumbLogoFile: File | null = null;
  fullLogoPreview: string | null = null;
  thumbPreview: string | null = null;
  fullLogoError: string = '';
  thumbLogoError: string = '';

  // Store original URLs
  originalFullLogoUrl: string | null = null;
  originalThumbLogoUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private coreService: CoreService,
    private route: ActivatedRoute
  ) {
    this.siteForm = this.createForm();
  }

  ngOnInit(): void {
    // Prefer @Input if provided, otherwise use route param
    if (!this.siteID) {
      const idParam = this.route.snapshot.paramMap.get('site');
      if (idParam) {
        this.siteID = Number(idParam);
      }
    }

    // Default/test companyID (adapt as needed)
    if (!this.companyID) {
      this.companyID = 1;
    }

    this.loadSiteData();
  }

  ngOnDestroy(): void {
    // Nothing to clean up now related to messages
  }

  createForm(): FormGroup {
    return this.fb.group({
      domainName: [''],
      siteUrl: [''],
      cmsType: ['']
    });
  }

  loadSiteData(): void {
    if (!this.siteID) {
      this.errorMessage = 'Site ID is required';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    this.coreService
      .getData(`/manageAccount/site/edit/${this.siteID}`, {
        companyID: this.companyID
      })
      .subscribe({
        next: (response: any) => {
          const siteData = response.data || response;
          console.log('Site data loaded:', siteData);

          this.siteForm.patchValue({
            domainName: siteData.domainName || '',
            siteUrl: siteData.siteUrl || siteData.SiteUrl || '',
            cmsType: siteData.cmsType || siteData.CmsType || ''
          });

          this.originalFullLogoUrl =
            siteData.fullLogoUrl || siteData.full_logo_url || null;
          this.originalThumbLogoUrl =
            siteData.thumbLogoUrl || siteData.thumb_logo_url || null;

          if (this.originalFullLogoUrl) {
            this.fullLogoPreview = this.originalFullLogoUrl;
          }
          if (this.originalThumbLogoUrl) {
            this.thumbPreview = this.originalThumbLogoUrl;
          }

          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading site data:', error);
          this.errorMessage =
            error.error?.message ||
            'Failed to load site data. Please try again.';
          this.isLoading = false;
        }
      });
  }

  onFileChange(event: any, type: 'full' | 'thumb'): void {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'image/png') {
      if (type === 'full') {
        this.fullLogoError = 'Only PNG files are allowed';
      } else {
        this.thumbLogoError = 'Only PNG files are allowed';
      }
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      if (type === 'full') {
        this.fullLogoError = 'File size must be less than 5MB';
      } else {
        this.thumbLogoError = 'File size must be less than 5MB';
      }
      return;
    }

    // Clear errors
    if (type === 'full') {
      this.fullLogoError = '';
      this.fullLogoFile = file;
    } else {
      this.thumbLogoError = '';
      this.thumbLogoFile = file;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e: any) => {
      if (type === 'full') {
        this.fullLogoPreview = e.target.result;
      } else {
        this.thumbPreview = e.target.result;
      }
    };
    reader.readAsDataURL(file);
  }

  onSubmit(): void {
    if (this.siteForm.invalid) {
      this.markFormGroupTouched(this.siteForm);
      return;
    }

    this.isSubmitting = true;
    this.successMessage = '';
    this.errorMessage = '';

    const formData = new FormData();

    formData.append('id', this.siteID.toString());
    formData.append('companyID', this.companyID.toString());
    formData.append(
      'domainName',
      this.siteForm.get('domainName')?.value || ''
    );
    formData.append('siteUrl', this.siteForm.get('siteUrl')?.value || '');
    formData.append('cmsType', this.siteForm.get('cmsType')?.value || '');

    if (this.fullLogoFile) {
      formData.append('fullLogoUrl', this.fullLogoFile, this.fullLogoFile.name);
    }
    if (this.thumbLogoFile) {
      formData.append(
        'thumbLogoUrl',
        this.thumbLogoFile,
        this.thumbLogoFile.name
      );
    }

    console.log('FormData entries being sent:');
    for (let pair of (formData as any).entries()) {
      console.log(pair[0] + ': ', pair[1]);
    }

    this.coreService
      .postData(`manageAccount/site/update/${this.siteID}`, formData)
      .subscribe({
        next: (response: any) => {
          console.log('Site updated successfully:', response);
          this.successMessage = 'Site updated successfully!';
        },
        error: (error) => {
          console.error('Error updating site:', error);
          this.errorMessage =
            error.error?.message ||
            'Failed to update site. Please try again.';
        },
        complete: () => {
          this.isSubmitting = false;
        }
      });
  }

  onCancel(): void {
    console.log('Edit cancelled');
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getControl(controlName: string) {
    return this.siteForm.get(controlName);
  }
}
